import { useState } from "react";
import { CalendarDays, Users, MapPin, BarChart3, Lock } from "lucide-react";
import { Loadable, useLoad, fmtDay, SelectField } from "@/components/control/kit";
import { V3Page, AmberAside, V3Actions } from "@/components/control/v3";

const ICONS = { support: CalendarDays, organisationen: Users, moderation: MapPin, freigaben: BarChart3 };

function Bars({ areas }) {
  const max = Math.max(1, ...areas.map((a) => a.count));
  return (
    <div className="rounded-2xl border border-[#E6E1F0] bg-white p-5" data-testid="ops-bars">
      <h2 className="text-lg font-bold text-brand-purple">Bearbeitete Vorgänge</h2>
      <ul className="mt-4 space-y-4">
        {areas.map((a, i) => (
          <li key={a.key}>
            <p className="text-sm font-semibold text-brand-purple">{a.label}</p>
            <div className="mt-1.5 flex items-center gap-4">
              <div className="h-2 flex-1 rounded-full bg-[#EEEAF7]" role="img" aria-label={`${a.label}: ${a.count}`}>
                <div className={`h-2 rounded-full ${i === 0 ? "bg-brand-magenta" : "bg-[#8A63C9]"}`} style={{ width: `${(a.count / max) * 100}%` }} />
              </div>
              <span className="w-8 text-right text-sm font-bold text-brand-purple" data-testid={`ops-count-${a.key}`}>{a.count}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function OpsEffort() {
  const [draft, setDraft] = useState("30");
  const [days, setDays] = useState("30");
  const st = useLoad(`/ops-effort?days=${days}`);
  return (
    <V3Page crumb="Übersicht" title="Betriebsaufwand nach Bereich" subtitle="Fallzahlen zeigen Arbeitsmenge und ersetzen keine Zeit- oder Kostenmessung." testId="ops-effort-page"
      aside={<>
        <AmberAside title="Aussagekraft" text="Zusagen und tatsächliche Teilnahme sind getrennte Ereignisse. Zahlen aus dem Audit-Log, keine Wirkungsmessung." />
        <div className="rounded-2xl border border-[#E6E1F0] bg-white p-4"><p className="flex items-center gap-2 font-bold text-brand-purple"><Lock className="h-5 w-5 text-brand-magenta" aria-hidden="true" />Privatsphäre</p>
          <p className="mt-2 text-sm text-zinc-600">Keine Bewertung von Einzelpersonen, Diagnosen oder Bewegungsprofilen.</p></div>
      </>}>
      <Loadable state={st}>
        {(d) => (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {d.areas.map((a) => {
                const Icon = ICONS[a.key];
                return (
                  <div key={a.key} className="rounded-2xl border border-[#E6E1F0] bg-white p-4" data-testid={`ops-kpi-${a.key}`}>
                    <p className="flex items-center gap-2 text-2xl font-bold text-brand-purple"><Icon className="h-5 w-5 text-brand-magenta" aria-hidden="true" />{a.count}</p>
                    <p className="mt-2 break-words text-sm font-semibold text-zinc-600 hyphens-auto" lang="de">{a.label}</p>
                  </div>
                );
              })}
            </div>
            <Bars areas={d.areas} />
            <p className="text-sm text-zinc-600" data-testid="ops-period">Zeitraum: seit {fmtDay(d.since)} ({d.days} Tage). {d.note}</p>
          </div>
        )}
      </Loadable>
      <div className="max-w-xs pt-2">
        <SelectField id="ops-days" label="Zeitraum" value={draft} onChange={setDraft} testId="ops-days"
          options={[{ value: "7", label: "Letzte 7 Tage" }, { value: "30", label: "Letzte 30 Tage" }, { value: "90", label: "Letzte 90 Tage" }]} />
      </div>
      <V3Actions primary="Zeitraum anwenden" onPrimary={() => setDays(draft)} secondary={null} testId="ops-apply" />
    </V3Page>
  );
}
